import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Editor, Block, CustomBlockDefinition } from '../editor/Editor';
import { SimplifiedDocumentWriter } from './SimplifiedDocumentWriter';
import { SimplifiedDocumentData } from './editor/ContentBlockWrapper';

/**
 * Props interface for DocumentWriterEditor component
 * 
 * This component can operate in two modes:
 * 1. Complex mode: Uses Block[] structure with full editor capabilities
 * 2. Simplified mode: Uses SimplifiedDocumentData for basic document editing
 */
interface DocumentWriterEditorProps {
  /** Initial blocks for complex editor mode */
  initialBlocks?: Block[];
  /** Initial data for simplified editor mode */
  initialData?: SimplifiedDocumentData;
  /** Callback when blocks change in complex mode */
  onChange?: (blocks: Block[]) => void;
  /** Callback when simplified data changes */
  onDataChange?: (data: SimplifiedDocumentData) => void;
  /** CSS class name for styling */
  className?: string;
  /** Whether to use simplified data structure instead of complex blocks */
  useSimplifiedData?: boolean;
  /** Game ID for organizing uploaded images */
  gameId?: string;
  /** Whether the editor is editable. When false, content cannot be modified. Defaults to true. */
  editable?: boolean;
  
  // Document creation and navigation callbacks
  onDocumentCreated?: (documentId: string) => void;
  onGlossaryItemEdit?: (glossaryItemId: string) => void;
  onDocumentDisplayEdit?: (documentId: string) => void;
  onDocumentDisplayNavigate?: (documentId: string) => void;
  
  // Document content management callbacks
  onDocumentContentUpdate?: (blockId: string, glossaryItemId: string, glossaryItemName: string) => void;
  onDocumentSave?: () => void;
  onDocumentBlockDelete?: (blockId: string) => void;
  onDocumentBlockCancel?: (blockId: string) => void;
}

/**
 * DocumentWriterEditor - A flexible document editor component that supports both
 * complex block-based editing and simplified data editing modes.
 * 
 * Features:
 * - Dual-mode operation (complex vs simplified)
 * - Glossary item integration
 * - Document display block support
 * - Change tracking and synchronization
 * - Custom block definitions support
 */
export const DocumentWriterEditor: React.FC<DocumentWriterEditorProps> = React.memo(({
  initialBlocks,
  initialData,
  onChange,
  onDataChange,
  className = "",
  useSimplifiedData = false,
  gameId,
  editable = true,
  onDocumentCreated,
  onGlossaryItemEdit,
  onDocumentDisplayEdit,
  onDocumentDisplayNavigate,
  onDocumentContentUpdate,
  onDocumentSave,
  onDocumentBlockDelete,
  onDocumentBlockCancel,
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  /** Internal state for blocks in complex mode */
  const [internalBlocks, setInternalBlocks] = useState<Block[]>(
    initialBlocks || [{ id: '1', type: 'text', content: '' }]
  );
  
  /** Internal state for simplified data */
  const [internalData, setInternalData] = useState<SimplifiedDocumentData | null>(
    initialData || null
  );
  
  /** Tracks whether the component has been initialized */
  const isInitialized = useRef(false);
  
  /** Reference to last initial blocks for change detection */
  const lastInitialBlocksRef = useRef<string>('');
  
  /** Reference to last initial data for change detection */
  const lastInitialDataRef = useRef<string>('');

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  /**
   * Creates and adds a new glossary item block to the document
   * @param glossaryItemId - Unique identifier for the glossary item
   * @param itemName - Display name for the glossary item
   */
  const handleAddGlossaryItemBlock = useCallback((glossaryItemId: string, itemName: string) => {
    const newBlock: Block = {
      id: `glossary-${Date.now()}`,
      type: 'glossary-item',
      content: '',
      metadata: {
        title: itemName,
        description: `Display ${itemName} data`,
        backgroundColor: "#ffffff",
        shadowType: "black",
        shadowColor: "#000000",
        glossaryItemId: glossaryItemId,
      },
    };

    const updatedBlocks = [...internalBlocks, newBlock];
    setInternalBlocks(updatedBlocks);
    onChange?.(updatedBlocks);
  }, [internalBlocks, onChange]);

  /**
   * Creates and adds a new document display block to the document
   * @param documentId - Unique identifier for the document to display
   * @param documentName - Display name for the document
   */
  const handleAddDocumentDisplayBlock = useCallback((documentId: string, documentName: string) => {
    const newBlock: Block = {
      id: `document-${Date.now()}`,
      type: 'document-display',
      content: '',
      metadata: {
        title: documentName,
        description: `Display ${documentName} document`,
        backgroundColor: "#ffffff",
        shadowType: "black",
        shadowColor: "#000000",
        documentId: documentId,
      },
    };

    const updatedBlocks = [...internalBlocks, newBlock];
    setInternalBlocks(updatedBlocks);
    onChange?.(updatedBlocks);
  }, [internalBlocks, onChange]);

  // ============================================================================
  // CUSTOM BLOCK DEFINITIONS
  // ============================================================================
  
  /**
   * Custom block definitions for the editor
   * Currently commented out but ready for future implementation
   */
  const customBlocks: CustomBlockDefinition[] = useMemo(() => [
    // TODO: Uncomment and implement when DocumentBlueprintBlock is ready
    /*
    {
      type: 'document-blueprint',
      label: 'Document Template',
      icon: <FileText size={16} />,
      description: 'Create a new document using a template',
      component: DocumentBlueprintBlock,
      initialMetadata: {
        title: "Create Document from Template",
        description: "Select a template and fill out the form to create a new document",
        backgroundColor: "#ffffff",
        shadowType: "black",
        shadowColor: "#000000"
      },
      additionalProps: {
        onDocumentCreated,
        onAddGlossaryItemBlock: handleAddGlossaryItemBlock,
        onAddDocumentDisplayBlock: handleAddDocumentDisplayBlock,
        allBlocks: internalBlocks
      }
    },
    */
  ], []);

  // ============================================================================
  // EFFECTS AND SYNCHRONIZATION
  // ============================================================================
  
  /**
   * Synchronizes internal blocks with external initialBlocks changes
   * Uses JSON stringification for deep comparison to avoid unnecessary updates
   */
  useEffect(() => {
    if (initialBlocks) {
      const initialBlocksString = JSON.stringify(initialBlocks);

      
      if (initialBlocksString !== lastInitialBlocksRef.current) {
        setInternalBlocks(initialBlocks);
        lastInitialBlocksRef.current = initialBlocksString;
        isInitialized.current = true;
      } else {
        console.log('DocumentWriterEditor: No update needed');
      }
    }
  }, [initialBlocks]);

  /**
   * Synchronizes internal data with external initialData changes
   * Only runs when useSimplifiedData is true
   */
  useEffect(() => {
    if (initialData && useSimplifiedData) {
      const initialDataString = JSON.stringify(initialData);
      
      if (initialDataString !== lastInitialDataRef.current) {
        setInternalData(initialData);
        lastInitialDataRef.current = initialDataString;
        isInitialized.current = true;
      }
    }
  }, [initialData, useSimplifiedData]);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================
  
  /**
   * Memoized change handler to prevent unnecessary re-renders
   * Updates internal state and notifies parent component
   */
  const handleChange = useMemo(() => (blocks: Block[]) => {
    setInternalBlocks(blocks);
    onChange?.(blocks);
  }, [onChange]);

  /**
   * Memoized props object for the Editor component
   * Always use the latest initialBlocks from parent to ensure proper synchronization
   */
  const editorProps = useMemo(() => ({
    initialBlocks: initialBlocks || internalBlocks,
    onChange: handleChange,
    customBlocks: customBlocks,
    gameId: gameId,
    editable: editable,
    onDocumentContentUpdate: onDocumentContentUpdate,
    onDocumentSave: onDocumentSave,
    onDocumentBlockDelete: onDocumentBlockDelete,
    onDocumentBlockCancel: onDocumentBlockCancel,
  }), [initialBlocks, internalBlocks, handleChange, customBlocks, gameId, editable, onDocumentContentUpdate, onDocumentSave, onDocumentBlockDelete, onDocumentBlockCancel]);

  /**
   * Handles changes in simplified data mode
   * Updates internal state and notifies parent component
   */
  const handleSimplifiedDataChange = useCallback((data: SimplifiedDocumentData) => {
    setInternalData(data);
    onDataChange?.(data);
  }, [onDataChange]);

  // ============================================================================
  // RENDER LOGIC
  // ============================================================================
  
  // Simplified mode: Render SimplifiedDocumentWriter
  if (useSimplifiedData) {
    return (
      <SimplifiedDocumentWriter
        initialData={internalData || initialData}
        onChange={handleSimplifiedDataChange}
        className={className}
        onDocumentCreated={onDocumentCreated}
        onGlossaryItemEdit={onGlossaryItemEdit}
        onDocumentDisplayEdit={onDocumentDisplayEdit}
        onDocumentDisplayNavigate={onDocumentDisplayNavigate}
      />
    );
  }

  // Complex mode: Render full Editor with block support
  return (
    <div className={`w-full h-full ${className}`}>
      <Editor {...editorProps} />
    </div>
  );
}); 